/**
 * The KPI dashboard's live queues (A-02): unfinished orders by status, and the paper-Rx
 * digitisation backlog. What is waiting is "now", whatever the dashboard's period; the
 * digitisation card's throughput follows the period. Both refresh every minute while open.
 *
 * Waiting past the API's stale threshold is shown with a warning icon and the words, never colour
 * alone.
 */

import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import { digitiseQueue, orderQueues, queuedOrders, type DigitiseQueue, type OrderQueue } from "../../api/analytics";
import { describeError } from "../../api/errors";
import { ageLabel, formatHours, formatNumber } from "../../lib/kpi";
import { QUEUED_ORDER_EXPORT } from "../../lib/kpiExports";
import { byPageNumber, EXPORT_PAGE_SIZE } from "../../lib/tableExport";
import { formatINR, formatINRCompact } from "../../lib/money";
import { timeAgo } from "../../lib/uploadQueue";
import { useNow } from "../../lib/useNow";
import { OrderStatusBadge } from "../OrderStatusBadge";
import { ExportButton } from "../ui/ExportButton";
import { Card, LoadError, ShareBar, Skeleton, WarningIcon } from "./KpiParts";

const LIVE_REFRESH_MS = 60_000;
const QUEUE_PAGE = 10;

const REASONS: Record<string, string> = {
  illegible: "Illegible",
  expired: "Expired",
  invalid: "Invalid",
  duplicate: "Duplicate",
  other: "Other",
};

function Stale({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 font-medium text-warning">
      <WarningIcon />
      {children}
    </span>
  );
}

function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-9" />
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ order queues

export function OrderQueuesCard() {
  const now = useNow(30_000);
  const q = useQuery({ queryKey: ["kpi", "order-queues"], queryFn: orderQueues, refetchInterval: LIVE_REFRESH_MS });
  const [open, setOpen] = useState<string | null>(null);
  const data = q.data;

  return (
    <Card
      title="Order queues"
      subtitle={data ? `Unfinished orders now · stale after ${data.stale_after_hours} hr in one status` : "Unfinished orders now"}
      action={data && <span className="text-xs text-ink-muted">Live · {timeAgo(data.as_of, now)}</span>}
    >
      {q.isLoading ? (
        <ListSkeleton rows={6} />
      ) : !data ? (
        <LoadError message={describeError(q.error)} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <p className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm text-ink-soft">
            <span>
              <strong className="text-lg font-semibold text-ink">{formatNumber(data.open_orders)}</strong> open
            </span>
            {data.stale_orders > 0 ? (
              <Stale>{formatNumber(data.stale_orders)} stale</Stale>
            ) : (
              <span className="text-ink-muted">none stale</span>
            )}
          </p>
          <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line">
            {data.queues.map((queue) => (
              <QueueRow
                key={queue.status_code}
                queue={queue}
                now={now}
                open={open === queue.status_code}
                onToggle={() => setOpen((was) => (was === queue.status_code ? null : queue.status_code))}
              />
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

function QueueRow({ queue, now, open, onToggle }: { queue: OrderQueue; now: number; open: boolean; onToggle: () => void }) {
  const empty = queue.count === 0;
  const notes = [
    queue.stale > 0 && <Stale key="stale">{formatNumber(queue.stale)} stale</Stale>,
    queue.unassigned > 0 && <span key="unassigned">{formatNumber(queue.unassigned)} unclaimed</span>,
    queue.oldest_since && <span key="oldest">oldest {ageLabel(queue.oldest_since, now)}</span>,
    Number(queue.amount) > 0 && <span key="amount">{formatINRCompact(queue.amount)}</span>,
  ].filter(Boolean);
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        disabled={empty}
        aria-expanded={empty ? undefined : open}
        className="grid w-full grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 px-3 py-2.5 text-left transition enabled:hover:bg-raised"
      >
        <span className="min-w-0">
          <OrderStatusBadge code={queue.status_code} label={queue.label} />
        </span>
        <span className="flex items-center gap-2">
          <span className={`text-base font-semibold tabular-nums ${empty ? "text-ink-faint" : "text-ink"}`}>{formatNumber(queue.count)}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={`h-4 w-4 text-ink-muted transition-transform ${open ? "rotate-180" : ""} ${empty ? "invisible" : ""}`} aria-hidden>
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
        {notes.length > 0 && (
          <span className="col-span-2 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ink-muted">{notes}</span>
        )}
      </button>
      {open && <QueuedOrders statusCode={queue.status_code} now={now} />}
    </li>
  );
}

function QueuedOrders({ statusCode, now }: { statusCode: string; now: number }) {
  const q = useInfiniteQuery({
    queryKey: ["kpi", "queue-orders", statusCode],
    queryFn: ({ pageParam }) => queuedOrders(statusCode, pageParam, QUEUE_PAGE),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    refetchInterval: LIVE_REFRESH_MS,
  });
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const total = q.data?.pages[0]?.total ?? 0;

  return (
    <div className="border-t border-line bg-raised px-3 py-2">
      {q.isLoading ? (
        <ListSkeleton rows={3} />
      ) : q.isError && rows.length === 0 ? (
        <LoadError message={describeError(q.error)} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 py-1">
            <p className="text-xs text-ink-muted">{formatNumber(total)} in this status, longest waiting first</p>
            {/* Every order in the status, not only those shown. */}
            <ExportButton
              table={`${statusCode.replace(/_/g, " ")} orders`}
              columns={QUEUED_ORDER_EXPORT}
              keyOf={(o) => o.id}
              fetchPage={byPageNumber((page) => queuedOrders(statusCode, page, EXPORT_PAGE_SIZE))}
            />
          </div>
          <ol className="divide-y divide-line text-sm">
            {rows.map((o) => (
              <li key={o.id} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 py-2">
                <span className="font-mono text-xs font-semibold text-ink">{o.order_id}</span>
                <span className="min-w-0 truncate text-ink-soft" title={o.patient_name ?? undefined}>
                  {o.patient_name ?? "—"}
                  <span className="text-ink-muted"> · {o.assignee_name ?? "unclaimed"}</span>
                  {o.final_amount && Number(o.final_amount) > 0 && (
                    <span className="hidden text-ink-muted sm:inline"> · {formatINR(o.final_amount)}</span>
                  )}
                </span>
                <span className="text-right text-xs tabular-nums text-ink-muted">
                  {o.stale ? <Stale>{ageLabel(o.in_status_since, now)}</Stale> : ageLabel(o.in_status_since, now)}
                </span>
              </li>
            ))}
          </ol>
          {q.hasNextPage && (
            <button
              type="button"
              onClick={() => void q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
              className="btn-ghost btn-sm mt-1 w-full"
            >
              {q.isFetchingNextPage ? "Loading…" : "Show more"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ digitisation queue

function Figure({ label, value, children }: { label: string; value: string; children?: ReactNode }) {
  return (
    <div className="min-w-0 rounded-xl border border-line bg-raised p-3">
      <div className="truncate text-xs text-ink-muted">{label}</div>
      <div className="mt-0.5 truncate text-lg font-semibold text-ink">{value}</div>
      {children && <div className="mt-0.5 text-xs text-ink-muted">{children}</div>}
    </div>
  );
}

export function DigitiseQueueCard({ days }: { days: number }) {
  const now = useNow(30_000);
  const q = useQuery({
    queryKey: ["kpi", "digitise", days],
    queryFn: () => digitiseQueue(days),
    placeholderData: keepPreviousData,
    refetchInterval: LIVE_REFRESH_MS,
  });
  const d = q.data;

  return (
    <Card title="Prescription digitisation" subtitle="Paper prescriptions patients uploaded" busy={q.isPlaceholderData}>
      {q.isLoading ? (
        <ListSkeleton rows={6} />
      ) : !d ? (
        <LoadError message={describeError(q.error)} onRetry={() => void q.refetch()} />
      ) : (
        <DigitiseBody d={d} days={days} now={now} />
      )}
    </Card>
  );
}

function DigitiseBody({ d, days, now }: { d: DigitiseQueue; days: number; now: number }) {
  const waiting = d.pending_review + d.in_review;
  const window = days === 1 ? "Today" : `Last ${days} days`;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Figure label="Waiting now" value={formatNumber(waiting)}>
          {formatNumber(d.pending_review)} new · {formatNumber(d.in_review)} in review
        </Figure>
        <Figure label={`Over ${d.stale_after_hours} hr`} value={formatNumber(d.stale)}>
          {d.stale > 0 ? <Stale>stale</Stale> : "none stale"}
        </Figure>
        <Figure label="Longest wait" value={d.oldest_waiting_since ? ageLabel(d.oldest_waiting_since, now) : "—"} />
      </div>

      <div>
        <h4 className="section-title text-xs">{window}</h4>
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          {(
            [
              ["Received", d.received],
              ["Digitised", d.digitized],
              ["Rejected", d.rejected],
              ["Cancelled", d.cancelled],
            ] as const
          ).map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-ink-muted">{label}</dt>
              <dd className="font-semibold tabular-nums text-ink">{formatNumber(value)}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-sm text-ink-soft">
          {d.median_turnaround_hours === null ? (
            "Nothing digitised in this period."
          ) : (
            <>
              Median <strong className="text-ink">{formatHours(d.median_turnaround_hours)}</strong> from upload to
              digitised; 9 in 10 within <strong className="text-ink">{formatHours(d.p90_turnaround_hours)}</strong>.
            </>
          )}
        </p>
      </div>

      {d.rejection_reasons.length > 0 && (
        <div>
          <h4 className="section-title text-xs">Why uploads were rejected</h4>
          <ul className="mt-2 space-y-2">
            {d.rejection_reasons.map((r) => (
              <li key={r.reason} className="grid grid-cols-[6rem_1fr_auto] items-center gap-3 text-sm">
                <span className="truncate text-ink-soft">{REASONS[r.reason] ?? r.reason}</span>
                <ShareBar fraction={d.rejected ? r.count / d.rejected : 0} />
                <span className="w-8 text-right tabular-nums text-ink">{formatNumber(r.count)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h4 className="section-title text-xs">Waiting longest</h4>
        {d.oldest.length === 0 ? (
          <p className="mt-2 text-sm text-ink-muted">Nothing waiting — the queue is clear.</p>
        ) : (
          <ol className="mt-1 divide-y divide-line text-sm">
            {d.oldest.map((u) => (
              <li key={u.id} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 py-2">
                <span className="font-mono text-xs text-ink-muted">#{u.id.slice(0, 8)}</span>
                <span className="min-w-0 truncate text-ink-soft">
                  {u.patient_name ?? "—"}
                  {u.family_member_name && <span className="text-ink-muted"> · for {u.family_member_name}</span>}
                  <span className="text-ink-muted">
                    {" · "}
                    {u.status === "in_review" ? `in review${u.reviewer_name ? ` (${u.reviewer_name})` : ""}` : "not started"}
                  </span>
                </span>
                <span className="text-right text-xs tabular-nums text-ink-muted">
                  {u.stale ? <Stale>{ageLabel(u.uploaded_at, now)}</Stale> : ageLabel(u.uploaded_at, now)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
